import { ButtonGradientPrimary, ButtonSecondary, Icon } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ObjectiveStatusValue } from '@/constants/ObjectiveStatus';

import { ObjectiveStatus } from '@/constants/ObjectiveStatus';

interface ObjectiveFormActionsProps {
    canSaveAsDraft: boolean;
    isSaving: boolean;
    /** The status of the save in flight, so only its own button spins. */
    savingStatus?: ObjectiveStatusValue;
    onChooseStatus: (status: ObjectiveStatusValue) => void;
    onCancel: () => void;
}

/** Cancel on one side; save as draft and publish, or save the changes, on the other. */
export default function ObjectiveFormActions({
    canSaveAsDraft,
    isSaving,
    savingStatus,
    onChooseStatus,
    onCancel,
}: ObjectiveFormActionsProps) {
    const { t } = useTranslation();

    return (
        <div className={'flex flex-wrap items-center justify-between gap-3'}>
            <ButtonSecondary
                onClick={onCancel}
                disabled={isSaving}
                className={'border-obj-line h-[45px] !rounded-[12px] border bg-white/80'}
                dataTestId={'objective-form-cancel'}
            >
                {t('objectives.form.cancel')}
            </ButtonSecondary>

            <div className={'flex flex-wrap items-center gap-3'}>
                {/* The form's first submit button, so it is what Enter presses: a keystroke
                    saves a draft and never publishes. */}
                {canSaveAsDraft && (
                    <ButtonSecondary
                        type={'submit'}
                        onClick={() => onChooseStatus(ObjectiveStatus.DRAFT)}
                        loading={savingStatus === ObjectiveStatus.DRAFT}
                        disabled={isSaving}
                        dataTestId={'objective-form-save-draft'}
                    >
                        {t('objectives.form.saveDraft')}
                    </ButtonSecondary>
                )}

                <ButtonGradientPrimary
                    type={'submit'}
                    onClick={() => onChooseStatus(ObjectiveStatus.PUBLISHED)}
                    loading={savingStatus === ObjectiveStatus.PUBLISHED}
                    disabled={isSaving}
                    leftIcon={
                        <Icon
                            name={'essential-tick-circle'}
                            size={'sm'}
                            color={'inherit'}
                        />
                    }
                    className={'h-[45px] !rounded-[12px]'}
                    dataTestId={'objective-form-save'}
                >
                    {t(canSaveAsDraft ? 'objectives.form.publish' : 'objectives.form.saveChanges')}
                </ButtonGradientPrimary>
            </div>
        </div>
    );
}
